import { useRouteOrganizationId } from '@/hooks/useRouteOrganizationId'
import queryClient from '@/lib/queryClient'
import { indexTableQueryOptions } from '@/lib/services/index-table-query-options'
import {
  buildAdminQueryRoot,
  buildScopedAdminQueryKey,
  type AdminQueryKey,
  type AdminQueryRoot,
  type OrganizationIdInput,
} from '@/lib/organization-scoping'
import { queryOptions, useMutation, useQuery, useSuspenseQuery } from '@tanstack/react-query'
import {
  createCustomerOccasionReminder,
  createCustomerPosCart,
  createCustomer,
  createCustomerPaymentMethodSetupIntent,
  deleteCustomerPaymentMethod,
  deleteCustomerOccasionReminder,
  getDuplicateCustomerByPhone,
  getCustomerBillingDefaults,
  getCustomerPaymentMethods,
  getCustomerPaymentStatus,
  getCustomerReceivables,
  getCustomer,
  getCustomerPosHistoryItems,
  getCustomerRecipientSuggestions,
  getCustomers,
  printCustomerStatement,
  sendCustomerGoogleReviewInvite,
  sendCustomerReviewInvite,
  sendCustomerStatement,
  softDeleteCustomer,
  storeCustomerPaymentMethod,
  updateCustomerBillingSettings,
  updateCustomerOccasionReminder,
  updateCustomer,
  updateCustomerInternalNotes,
  type CustomerIndexQueryKeyEntries,
  type CustomerOccasionReminderScope,
  type CustomerOrganizationScope,
  type CustomerPaymentMethodsQueryKeyEntries,
  type CustomerPosHistoryQueryKeyEntries,
  type CustomerRecipientSuggestionsQueryKeyEntries,
  type CustomerScope,
} from './customers.api'
import type {
  CreateCustomerInput,
  Customer,
  CustomerBillingSettingsInput,
  CustomerIdInput,
  CustomerIndexFilters,
  CustomerIndexResponse,
  CustomerOccasionReminderInput,
  CustomerRecipientSuggestionFilters,
  CustomerStatement,
  DuplicateCustomerPhoneInput,
  StoreCustomerPaymentMethodInput,
  UpdateCustomerInput,
  UpdateCustomerInternalNotesInput,
} from './customers.types'
import { salesOrderQueryKeys } from '@/lib/services/sales-orders/sales-orders.query'
import { cartQueryKeys } from '@/lib/services/carts/carts.query'

interface OrganizationScope {
  organizationId?: OrganizationIdInput
}

/** Customer ids arrive from route params, payloads, or not at all before a customer is chosen. */
type OptionalCustomerId = CustomerIdInput | null | undefined

interface CustomerKeyScope extends OrganizationScope {
  customerId?: OptionalCustomerId
}

interface CustomerIndexScope<
  TFilters extends CustomerIndexFilters | undefined = CustomerIndexFilters | undefined,
> extends OrganizationScope {
  filters?: TFilters | undefined
}

interface CustomerRecipientSuggestionsScope extends CustomerKeyScope {
  filters?: CustomerRecipientSuggestionFilters | undefined
}

/** Its fetcher calls the endpoint itself rather than reading the key, so it needs the organization. */
interface CustomerPaymentStatusScope extends CustomerOrganizationScope {
  customerId?: OptionalCustomerId
  paymentId?: OptionalCustomerId
}

interface CustomerPaymentMethodsScope extends CustomerKeyScope {
  paymentEntrySessionId?: number | null | undefined
}

interface CustomerPosHistoryScope extends CustomerKeyScope {
  limit?: number | undefined
}

/** The key entries each fetcher reads; the scoped key builders below set them. */
type CustomerIndexQueryKey<
  TFilters extends CustomerIndexFilters | undefined = CustomerIndexFilters | undefined,
> = [AdminQueryRoot & CustomerIndexQueryKeyEntries<TFilters>]
type CustomerShowQueryKey = [AdminQueryRoot & CustomerScope]
type CustomerRecipientSuggestionsQueryKey = [
  AdminQueryRoot & CustomerRecipientSuggestionsQueryKeyEntries,
]
type CustomerPaymentMethodsQueryKey = [AdminQueryRoot & CustomerPaymentMethodsQueryKeyEntries]
type CustomerPosHistoryQueryKey = [AdminQueryRoot & CustomerPosHistoryQueryKeyEntries]

/** Query options a caller may override; the key and fetcher stay owned by this module. */
type QueryParams<TOptions> = Partial<Omit<TOptions, 'queryKey' | 'queryFn'>>

/** Mutations that act on one customer take its id with the request fields. */
interface CustomerMutationValues {
  customerId: CustomerIdInput
}

function getCustomersFromIndexResponse(response: CustomerIndexResponse | undefined) {
  return Array.isArray(response) ? response : (response?.data ?? [])
}

export const customerQueryKeys = {
  all: ({ organizationId }: OrganizationScope = {}): AdminQueryKey => [
    buildAdminQueryRoot({ entity: 'customer' }, { organizationId }),
  ],
  index: <TFilters extends CustomerIndexFilters | undefined = CustomerIndexFilters | undefined>({
    organizationId,
    filters,
  }: CustomerIndexScope<TFilters> = {}): CustomerIndexQueryKey<TFilters> =>
    buildScopedAdminQueryKey(customerQueryKeys.all({ organizationId })[0], {
      organizationId,
      scope: 'index',
      filters,
    }),
  show: ({ organizationId, customerId }: CustomerKeyScope = {}): CustomerShowQueryKey =>
    buildScopedAdminQueryKey(customerQueryKeys.all({ organizationId })[0], {
      organizationId,
      scope: 'show',
      customerId: Number(customerId),
    }),
  billingDefaults: ({ organizationId }: OrganizationScope = {}) =>
    buildScopedAdminQueryKey(customerQueryKeys.all({ organizationId })[0], {
      organizationId,
      scope: 'billingDefaults',
    }),
  recipientSuggestions: ({
    organizationId,
    customerId,
    filters,
  }: CustomerRecipientSuggestionsScope = {}): CustomerRecipientSuggestionsQueryKey =>
    buildScopedAdminQueryKey(customerQueryKeys.all({ organizationId })[0], {
      organizationId,
      scope: 'recipientSuggestions',
      customerId: Number(customerId),
      filters,
    }),
  receivables: ({ organizationId, customerId }: CustomerKeyScope = {}): CustomerShowQueryKey =>
    buildScopedAdminQueryKey(customerQueryKeys.all({ organizationId })[0], {
      organizationId,
      scope: 'receivables',
      customerId: Number(customerId),
    }),
  printStatement: ({ organizationId, customerId }: CustomerKeyScope = {}): CustomerShowQueryKey =>
    buildScopedAdminQueryKey(customerQueryKeys.all({ organizationId })[0], {
      organizationId,
      scope: 'printStatement',
      customerId: Number(customerId),
    }),
  paymentStatus: ({ organizationId, customerId, paymentId }: CustomerPaymentStatusScope) =>
    buildScopedAdminQueryKey(customerQueryKeys.all({ organizationId })[0], {
      organizationId,
      scope: 'paymentStatus',
      customerId: Number(customerId),
      paymentId: Number(paymentId),
    }),
  paymentMethods: ({
    organizationId,
    customerId,
    paymentEntrySessionId,
  }: CustomerPaymentMethodsScope = {}): CustomerPaymentMethodsQueryKey =>
    buildScopedAdminQueryKey(customerQueryKeys.all({ organizationId })[0], {
      organizationId,
      scope: 'paymentMethods',
      customerId: Number(customerId),
      ...(paymentEntrySessionId ? { paymentEntrySessionId } : {}),
    }),
  posHistoryItems: ({
    organizationId,
    customerId,
    limit,
  }: CustomerPosHistoryScope = {}): CustomerPosHistoryQueryKey =>
    buildScopedAdminQueryKey(customerQueryKeys.all({ organizationId })[0], {
      organizationId,
      scope: 'posHistoryItems',
      customerId: Number(customerId),
      limit,
    }),
}

export const customerMutationKeys = {
  checkDuplicatePhone: ['customer', 'checkDuplicatePhone'],
  createPosCart: ['customer', 'createPosCart'],
  createOccasionReminder: ['customer', 'createOccasionReminder'],
  updateOccasionReminder: ['customer', 'updateOccasionReminder'],
  deleteOccasionReminder: ['customer', 'deleteOccasionReminder'],
  createPaymentMethodSetupIntent: ['customer', 'createPaymentMethodSetupIntent'],
  storePaymentMethod: ['customer', 'storePaymentMethod'],
  deletePaymentMethod: ['customer', 'deletePaymentMethod'],
  prepareCardPayment: ['customer', 'prepareCardPayment'],
  createCashPayment: ['customer', 'createCashPayment'],
  createCheckPayment: ['customer', 'createCheckPayment'],
  sendCardPaymentToReader: ['customer', 'sendCardPaymentToReader'],
}

export const customerQueries = {
  index: <TFilters extends CustomerIndexFilters | undefined = CustomerIndexFilters | undefined>({
    organizationId,
    filters,
  }: CustomerIndexScope<TFilters> = {}) =>
    indexTableQueryOptions({
      queryKey: customerQueryKeys.index({ organizationId, filters }),
      queryFn: getCustomers<TFilters>,
    }),

  show: ({ organizationId, customerId }: CustomerKeyScope = {}) =>
    queryOptions({
      queryKey: customerQueryKeys.show({ organizationId, customerId }),
      queryFn: getCustomer,
      // Use data from the sales order index route if available
      placeholderData: () =>
        getCustomersFromIndexResponse(
          queryClient.getQueryData<CustomerIndexResponse>(
            customerQueryKeys.index({ organizationId })
          )
        ).find((o) => o.id === Number(customerId)),
    }),

  billingDefaults: ({ organizationId }: OrganizationScope = {}) =>
    queryOptions({
      queryKey: customerQueryKeys.billingDefaults({ organizationId }),
      queryFn: getCustomerBillingDefaults,
    }),

  recipientSuggestions: ({
    organizationId,
    customerId,
    filters,
  }: CustomerRecipientSuggestionsScope = {}) =>
    queryOptions({
      queryKey: customerQueryKeys.recipientSuggestions({ organizationId, customerId, filters }),
      queryFn: getCustomerRecipientSuggestions,
    }),

  receivables: ({ organizationId, customerId }: CustomerKeyScope = {}) =>
    queryOptions({
      queryKey: customerQueryKeys.receivables({ organizationId, customerId }),
      queryFn: getCustomerReceivables,
      staleTime: 0,
    }),

  printStatement: ({ organizationId, customerId }: CustomerKeyScope = {}) =>
    queryOptions({
      queryKey: customerQueryKeys.printStatement({ organizationId, customerId }),
      queryFn: printCustomerStatement,
      staleTime: 0,
    }),

  paymentStatus: ({ organizationId, customerId, paymentId }: CustomerPaymentStatusScope) =>
    queryOptions({
      queryKey: customerQueryKeys.paymentStatus({ organizationId, customerId, paymentId }),
      queryFn: () => getCustomerPaymentStatus({ organizationId, customerId, paymentId }),
      enabled: Boolean(customerId && paymentId),
      staleTime: 0,
    }),

  paymentMethods: ({
    organizationId,
    customerId,
    paymentEntrySessionId,
  }: CustomerPaymentMethodsScope = {}) =>
    queryOptions({
      queryKey: customerQueryKeys.paymentMethods({
        organizationId,
        customerId,
        paymentEntrySessionId,
      }),
      queryFn: getCustomerPaymentMethods,
      enabled: Boolean(customerId),
      staleTime: 0,
    }),

  posHistoryItems: ({ organizationId, customerId, limit = 12 }: CustomerPosHistoryScope = {}) =>
    queryOptions({
      queryKey: customerQueryKeys.posHistoryItems({ organizationId, customerId, limit }),
      queryFn: getCustomerPosHistoryItems,
      enabled: Boolean(customerId),
    }),
}

export function useCustomers({
  organizationId,
  filters,
  ...params
}: CustomerIndexScope & QueryParams<ReturnType<typeof customerQueries.index>> = {}) {
  const resolvedOrganizationId = useRouteOrganizationId(organizationId)

  return useQuery({
    ...customerQueries.index({ organizationId: resolvedOrganizationId, filters }),
    ...params,
  })
}

export function useCustomer(
  customerId: OptionalCustomerId,
  {
    organizationId,
    ...params
  }: OrganizationScope & QueryParams<ReturnType<typeof customerQueries.show>> = {}
) {
  const resolvedOrganizationId = useRouteOrganizationId(organizationId)

  return useSuspenseQuery({
    ...customerQueries.show({ organizationId: resolvedOrganizationId, customerId }),
    ...params,
  })
}

export function useCustomerBillingDefaults({
  organizationId,
  ...params
}: OrganizationScope & QueryParams<ReturnType<typeof customerQueries.billingDefaults>> = {}) {
  const resolvedOrganizationId = useRouteOrganizationId(organizationId)

  return useSuspenseQuery({
    ...customerQueries.billingDefaults({ organizationId: resolvedOrganizationId }),
    ...params,
  })
}

export function useSearchCustomers({
  organizationId,
  filters,
  enabled = true,
  ...params
}: CustomerIndexScope & { enabled?: boolean } & Omit<
    QueryParams<ReturnType<typeof customerQueries.index>>,
    'enabled'
  > = {}) {
  const resolvedOrganizationId = useRouteOrganizationId(organizationId)
  const search = filters?.search

  return useQuery({
    ...customerQueries.index({ organizationId: resolvedOrganizationId, filters }),
    enabled: enabled && (!search || search.length >= 2),
    throwOnError: false,
    ...params,
  })
}

export function useCustomerReceivables(
  customerId: OptionalCustomerId,
  {
    organizationId,
    enabled,
    ...params
  }: OrganizationScope & QueryParams<ReturnType<typeof customerQueries.receivables>> = {}
) {
  const resolvedOrganizationId = useRouteOrganizationId(organizationId)

  return useQuery({
    ...customerQueries.receivables({
      organizationId: resolvedOrganizationId,
      customerId,
    }),
    enabled: enabled ?? Boolean(customerId),
    ...params,
  })
}

export function useCustomerPaymentStatus(
  customerId: OptionalCustomerId,
  paymentId: OptionalCustomerId,
  options: QueryParams<ReturnType<typeof customerQueries.paymentStatus>> = {}
) {
  const organizationId = useRouteOrganizationId()

  return useQuery({
    ...customerQueries.paymentStatus({ organizationId, customerId, paymentId }),
    ...options,
  })
}

export function useCustomerPaymentMethods(
  customerId: OptionalCustomerId,
  {
    organizationId,
    enabled,
    paymentEntrySessionId,
    ...params
  }: CustomerPaymentMethodsScope &
    QueryParams<ReturnType<typeof customerQueries.paymentMethods>> = {}
) {
  const resolvedOrganizationId = useRouteOrganizationId(organizationId)

  return useQuery({
    ...customerQueries.paymentMethods({
      organizationId: resolvedOrganizationId,
      customerId,
      paymentEntrySessionId,
    }),
    enabled: enabled ?? Boolean(customerId),
    ...params,
  })
}

export function useCustomerPosHistoryItems(
  customerId: OptionalCustomerId,
  {
    organizationId,
    limit = 12,
    ...params
  }: CustomerPosHistoryScope & QueryParams<ReturnType<typeof customerQueries.posHistoryItems>> = {}
) {
  const resolvedOrganizationId = useRouteOrganizationId(organizationId)

  return useQuery({
    ...customerQueries.posHistoryItems({
      organizationId: resolvedOrganizationId,
      customerId,
      limit,
    }),
    ...params,
  })
}

export function useCreateCustomerMutation() {
  const organizationId = useRouteOrganizationId()
  return useMutation({
    mutationFn: (values: CreateCustomerInput) => createCustomer({ organizationId, ...values }),
    onSuccess: () =>
      queryClient.invalidateQueries({ queryKey: customerQueryKeys.all({ organizationId }) }),
  })
}

const invalidateCustomerDependentQueries = (organizationId: number) =>
  Promise.all([
    queryClient.invalidateQueries({ queryKey: customerQueryKeys.all({ organizationId }) }),
    queryClient.invalidateQueries({ queryKey: cartQueryKeys.all({ organizationId }) }),
    queryClient.invalidateQueries({ queryKey: salesOrderQueryKeys.all({ organizationId }) }),
  ])

export function useUpdateCustomerMutation() {
  const organizationId = useRouteOrganizationId()
  return useMutation({
    mutationFn: (values: UpdateCustomerInput & CustomerMutationValues) =>
      updateCustomer({ organizationId, ...values }),
    onSuccess: () => invalidateCustomerDependentQueries(organizationId),
  })
}

export function useCheckDuplicateCustomerPhoneMutation() {
  const organizationId = useRouteOrganizationId()

  return useMutation({
    mutationKey: customerMutationKeys.checkDuplicatePhone,
    mutationFn: (values: DuplicateCustomerPhoneInput) =>
      getDuplicateCustomerByPhone({ organizationId, ...values }),
  })
}

export function useUpdateCustomerBillingSettingsMutation() {
  const organizationId = useRouteOrganizationId()

  return useMutation({
    mutationFn: (values: CustomerBillingSettingsInput & CustomerMutationValues) =>
      updateCustomerBillingSettings({ organizationId, ...values }),
    onSuccess: () => invalidateCustomerDependentQueries(organizationId),
  })
}

export function useUpdateCustomerInternalNotesMutation() {
  const organizationId = useRouteOrganizationId()

  return useMutation({
    mutationFn: (values: UpdateCustomerInternalNotesInput & CustomerMutationValues) =>
      updateCustomerInternalNotes({ organizationId, ...values }),
    onSuccess: () =>
      queryClient.invalidateQueries({ queryKey: customerQueryKeys.all({ organizationId }) }),
  })
}

const cleanupDeletedCustomerQueries = ({
  organizationId,
  customerId,
}: {
  organizationId: number
  customerId: OptionalCustomerId
}) => {
  queryClient.removeQueries({
    queryKey: customerQueryKeys.show({ organizationId, customerId }),
  })

  return queryClient.invalidateQueries({ queryKey: customerQueryKeys.all({ organizationId }) })
}

export function useSoftDeleteCustomerMutation({
  cleanupOnSuccess = true,
}: { cleanupOnSuccess?: boolean } = {}) {
  const organizationId = useRouteOrganizationId()
  const cleanupDeletedCustomer = (customerId: OptionalCustomerId) =>
    cleanupDeletedCustomerQueries({ organizationId, customerId })

  const mutation = useMutation({
    mutationFn: (customerId: CustomerIdInput) => softDeleteCustomer({ organizationId, customerId }),
    onSuccess: (_, customerId) => {
      if (!cleanupOnSuccess) {
        return undefined
      }

      return cleanupDeletedCustomer(customerId)
    },
  })

  return {
    ...mutation,
    cleanupDeletedCustomer,
  }
}

export function useCreateCustomerPosCartMutation() {
  const organizationId = useRouteOrganizationId()

  return useMutation({
    mutationKey: customerMutationKeys.createPosCart,
    mutationFn: (customerId: CustomerIdInput) =>
      createCustomerPosCart({ organizationId, customerId }),
    onSuccess: () =>
      queryClient.invalidateQueries({ queryKey: customerQueryKeys.all({ organizationId }) }),
  })
}

export function useCreateCustomerOccasionReminderMutation() {
  const organizationId = useRouteOrganizationId()

  return useMutation({
    mutationKey: customerMutationKeys.createOccasionReminder,
    mutationFn: (values: CustomerOccasionReminderInput & CustomerMutationValues) =>
      createCustomerOccasionReminder({ organizationId, ...values }),
    onSuccess: (_response, { customerId }) =>
      queryClient.invalidateQueries({
        queryKey: customerQueryKeys.show({ organizationId, customerId }),
      }),
  })
}

export function useUpdateCustomerOccasionReminderMutation() {
  const organizationId = useRouteOrganizationId()

  return useMutation({
    mutationKey: customerMutationKeys.updateOccasionReminder,
    mutationFn: (
      values: CustomerOccasionReminderInput &
        CustomerMutationValues &
        Pick<CustomerOccasionReminderScope, 'reminderId'>
    ) => updateCustomerOccasionReminder({ organizationId, ...values }),
    onSuccess: (_response, { customerId }) =>
      queryClient.invalidateQueries({
        queryKey: customerQueryKeys.show({ organizationId, customerId }),
      }),
  })
}

export function useDeleteCustomerOccasionReminderMutation() {
  const organizationId = useRouteOrganizationId()

  return useMutation({
    mutationKey: customerMutationKeys.deleteOccasionReminder,
    mutationFn: (
      values: CustomerMutationValues & Pick<CustomerOccasionReminderScope, 'reminderId'>
    ) => deleteCustomerOccasionReminder({ organizationId, ...values }),
    onSuccess: (_response, { customerId }) =>
      queryClient.invalidateQueries({
        queryKey: customerQueryKeys.show({ organizationId, customerId }),
      }),
  })
}

export function useSendCustomerStatementMutation() {
  const organizationId = useRouteOrganizationId()

  return useMutation({
    mutationFn: ({ customerId, email }: CustomerMutationValues & { email?: string | null }) =>
      sendCustomerStatement({ organizationId, customerId, email }),
    onSuccess: (_, { customerId }) =>
      queryClient.invalidateQueries({
        queryKey: customerQueryKeys.receivables({ organizationId, customerId }),
      }),
  })
}

export function useSendCustomerReviewInviteMutation() {
  const organizationId = useRouteOrganizationId()

  return useMutation({
    mutationFn: ({ customerId }: CustomerMutationValues) =>
      sendCustomerReviewInvite({ organizationId, customerId }),
    onSuccess: (_, { customerId }) =>
      queryClient.invalidateQueries({
        queryKey: customerQueryKeys.show({ organizationId, customerId }),
      }),
  })
}

export function useSendCustomerGoogleReviewInviteMutation() {
  const organizationId = useRouteOrganizationId()

  return useMutation({
    mutationFn: ({ customerId }: CustomerMutationValues) =>
      sendCustomerGoogleReviewInvite({ organizationId, customerId }),
    onSuccess: (_, { customerId }) =>
      queryClient.invalidateQueries({
        queryKey: customerQueryKeys.show({ organizationId, customerId }),
      }),
  })
}

export function useDeleteCustomerPaymentMethodMutation() {
  const organizationId = useRouteOrganizationId()

  return useMutation({
    mutationKey: customerMutationKeys.deletePaymentMethod,
    mutationFn: (values: CustomerMutationValues & { paymentMethodId: string }) =>
      deleteCustomerPaymentMethod({ organizationId, ...values }),
    onSuccess: (response, variables) => {
      const nextCards = response?.data ?? null

      if (Array.isArray(nextCards)) {
        queryClient.setQueryData(
          customerQueryKeys.paymentMethods({
            organizationId,
            customerId: variables.customerId,
          }),
          { data: nextCards }
        )
      }

      return queryClient.invalidateQueries({
        queryKey: customerQueryKeys.paymentMethods({
          organizationId,
          customerId: variables.customerId,
        }),
      })
    },
  })
}

export function useCreateCustomerPaymentMethodSetupIntentMutation() {
  const organizationId = useRouteOrganizationId()

  return useMutation({
    mutationKey: customerMutationKeys.createPaymentMethodSetupIntent,
    mutationFn: (values: CustomerMutationValues) =>
      createCustomerPaymentMethodSetupIntent({ organizationId, ...values }),
  })
}

export function useStoreCustomerPaymentMethodMutation() {
  const organizationId = useRouteOrganizationId()

  return useMutation({
    mutationKey: customerMutationKeys.storePaymentMethod,
    mutationFn: (values: StoreCustomerPaymentMethodInput & CustomerMutationValues) =>
      storeCustomerPaymentMethod({ organizationId, ...values }),
    onSuccess: (response, variables) => {
      const nextCards = response?.data ?? null

      if (Array.isArray(nextCards)) {
        queryClient.setQueryData(
          customerQueryKeys.paymentMethods({
            organizationId,
            customerId: variables.customerId,
          }),
          { data: nextCards }
        )
      }

      return queryClient.invalidateQueries({
        queryKey: customerQueryKeys.paymentMethods({
          organizationId,
          customerId: variables.customerId,
        }),
      })
    },
  })
}
